import type { ReactNode } from 'react';
import { cn } from '../lib/utils';

interface CollapsibleContainerProps {
  isOpen: boolean;
  children: ReactNode;
  className?: string;
  duration?: 150 | 200;
}

/**
 * CollapsibleContainer
 *
 * An animated container that smoothly expands/collapses using CSS grid transitions.
 * Commonly used for showing/hiding error messages, alerts, or conditional content.
 *
 * @example
 * ```tsx
 * <CollapsibleContainer isOpen={hasError} className="mb-4">
 *   <Alert variant="destructive">{errorMessage}</Alert>
 * </CollapsibleContainer>
 * ```
 */
export function CollapsibleContainer({
  isOpen,
  children,
  className,
  duration = 200,
}: CollapsibleContainerProps) {
  return (
    <div
      className={cn(
        'grid overflow-hidden transition-[grid-template-rows,margin] ease-in-out',
        duration === 200 ? 'duration-200' : 'duration-150',
        isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
        isOpen ? className : undefined
      )}
    >
      <div className="overflow-hidden min-h-0">{children}</div>
    </div>
  );
}
